/**
 * CLINIC-CATALOG-PRICING-TRUTH-S1-V1 (Refs #315) -- the treatment-catalog
 * BROWSE page (/dashboard/treatment-catalog).
 *
 * THE JOB: the catalog audience (doctor + leadership) reads the org's
 * treatment master with REAL price truth: the stepwise
 * PROGRESSIVE-DISCLOSURE walk (clinic scope -> service group -> procedure
 * -> only that procedure's price/provenance fields), price SOURCE always
 * visible (Clinic price / Chain rate / "Price not configured" -- never a
 * silently substituted number), active/inactive provenance for the
 * leadership tier. READ-ONLY: catalog editing has NO surface (founder
 * Studio seed only; the V5-P5/V8-P7 admin seam -- named, not built).
 *
 * SCOPE: clinic options come from the PHI-safe /me clinicOptions signal
 * via the ONE global clinic context (UI narrowing within server-verified
 * scope); every read passes the backend org/clinic fences (?clinicId
 * scope-honest 403). "Org-wide defaults" is the unresolved provenance
 * view (rows ship with clinicId visible); a clinic scope is the RESOLVED
 * view (an active clinic override shadows the org row server-side).
 *
 * HONEST STATES: loading / load-failure / pre-apply not-available /
 * empty-catalog all render visibly (never a blank, never a crash).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchTreatmentCatalog,
  type TreatmentCatalogItemView,
  type TreatmentCatalogResult,
} from '@/dashboard/api';
import { clinicOptionLabel, useClinicLens } from '@/dashboard/clinic-context';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  AcBadge,
  ClinicScopeLabel,
  EmptyState,
  ErrorState,
  FieldGrid,
  LoadingState,
  PageHeader,
  SectionShell,
} from '@/dashboard/ui';
import {
  catalogPriceDisplay,
  catalogPriceSource,
  catalogPriceSourceLabel,
  filterCatalogGroups,
  filterItemsWithinGroup,
  PRICE_SETTER_NAMING_COPY,
  pricingUnitLabel,
  typeaheadCatalogResults,
  typeaheadFlatItems,
} from '@/dashboard/doctor/treatment-picker-view';
import {
  canBrowseInactive,
  catalogDetailFields,
  defaultCatalogScope,
} from '@/dashboard/catalog/catalog-browse-view';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly result: TreatmentCatalogResult };

export default function TreatmentCatalogPage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  const clinicCtx = useClinicLens();
  const me = meCtx?.me ?? null;
  const options = clinicCtx?.options ?? [];

  // Disclosure level 0: clinic scope (null = org-wide defaults).
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2). The
  // lens maps onto this surface exactly: a picked clinic = that clinic's
  // resolved prices; "All clinics" = the org-wide defaults view. It was seeded
  // ONCE from the lens and then forked, so switching clinics in the header left
  // the catalog on the old clinic's prices -- on a pricing surface.
  const scope = defaultCatalogScope(clinicCtx?.selectedClinicId ?? null, options);
  // Leadership-only provenance toggle (doctor reads stay ACTIVE-only --
  // the server enforces this regardless of the query flag).
  const [includeInactive, setIncludeInactive] = useState(false);
  // Disclosure levels 1-3.
  const [group, setGroup] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  // STALE-RESPONSE FENCE: only the LATEST request may write the load
  // state -- a slower older response (rapid scope/inactive flips) must
  // never overwrite a newer scope's truth with stale rows.
  const requestSeq = useRef(0);

  const reload = useCallback((): void => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    setLoad({ kind: 'loading' });
    fetchTreatmentCatalog(scope, { includeInactive })
      .then((result) => {
        if (requestSeq.current === seq) setLoad({ kind: 'ready', result });
      })
      .catch((err: unknown) => {
        if (requestSeq.current === seq) {
          setLoad({
            kind: 'error',
            message:
              err instanceof Error ? err.message : 'Catalog load failed.',
          });
        }
      });
  }, [scope, includeInactive]);

  useEffect(() => {
    reload();
  }, [reload]);

  // The disclosure walk (group -> procedure -> query) belongs to ONE scope.
  // The removed in-place select cleared it on every change; now that the lens
  // owns the scope, the reset follows the LENS -- otherwise switching clinics
  // would leave another clinic's chosen procedure selected on a pricing page.
  useEffect(() => {
    setGroup(null);
    setSelectedId(null);
    setQuery('');
  }, [scope]);

  const scopeLabel =
    scope === null
      ? 'Org-wide defaults'
      : clinicOptionLabel(
          options.find((o) => o.clinicId === scope) ?? {
            clinicId: scope,
            clinicLabel: null,
          },
        );

  return (
    <div className="dashboard-page" data-testid="treatment-catalog-page">
      <PageHeader
        title="Treatment catalog"
        subs={[
          'Master services and real price truth for your scope. Read-only: prices are founder-seeded from the tariff.',
        ]}
      />
      <SectionShell
        eyebrow="Scope"
        title={scopeLabel}
        sub="Pick the clinic whose resolved prices you want; org-wide shows the chain defaults and every override unresolved."
        testId="catalog-scope"
      >
        <div className="catalog-browse__scope">
          {/* 119-CLINIC-OPERATING-SPINE-V1 (Phase 2): the catalog's OWN clinic
              select is gone -- a second control for a value the header lens
              owns. The scope is NAMED here instead; switching is the lens. */}
          <ClinicScopeLabel label={scopeLabel} testId="catalog-scope-select" />
          {canBrowseInactive(me) ? (
            <label className="clinic-workbench__field catalog-browse__inactive">
              <span>Include inactive (provenance)</span>
              <input
                type="checkbox"
                data-testid="catalog-include-inactive"
                checked={includeInactive}
                onChange={(e) => {
                  // The toggle changes the DATASET -- reset the disclosure
                  // ladder like a scope change (a group that exists only
                  // via inactive rows must not strand the level header).
                  setIncludeInactive(e.target.checked);
                  setGroup(null);
                  setSelectedId(null);
                  setQuery('');
                }}
              />
            </label>
          ) : null}
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="Catalog"
        title={group === null ? 'Service groups' : group}
        sub={
          group === null
            ? 'Pick a group to see its procedures - only relevant choices at each step.'
            : 'Procedures in this group; pick one for its price and provenance.'
        }
        testId="catalog-browse"
      >
        {load.kind === 'loading' ? (
          <LoadingState label="Loading the catalog" />
        ) : load.kind === 'error' ? (
          <ErrorState
            title="The catalog could not load"
            detail={load.message}
            action={
              <button
                type="button"
                className="treatment-picker__escape"
                data-testid="catalog-retry"
                onClick={reload}
              >
                Retry
              </button>
            }
            testId="catalog-error"
          />
        ) : !load.result.available ? (
          // PRE-APPLY honesty (the D-6 fail-safe law): the pricing contract
          // is not applied in this environment -- say so, never a blank.
          <EmptyState
            title="Catalog not available yet"
            body="The catalog pricing contract is not applied in this environment. The founder applies the migration and seed in Studio."
            testId="catalog-not-available"
          />
        ) : load.result.items.length === 0 ? (
          <EmptyState
            title="No catalog entries"
            body="The catalog is empty for this scope."
            testId="catalog-empty"
          />
        ) : (
          <CatalogBrowse
            result={load.result}
            group={group}
            selectedId={selectedId}
            query={query}
            scopeIsClinic={scope !== null}
            onQuery={setQuery}
            onGroup={(g) => {
              setGroup(g);
              setSelectedId(null);
              setQuery('');
            }}
            onSelect={setSelectedId}
          />
        )}
      </SectionShell>
    </div>
  );
}

export function CatalogBrowse({
  result,
  group,
  selectedId,
  query,
  scopeIsClinic,
  onQuery,
  onGroup,
  onSelect,
}: {
  result: TreatmentCatalogResult;
  group: string | null;
  selectedId: number | null;
  query: string;
  scopeIsClinic: boolean;
  onQuery: (q: string) => void;
  onGroup: (g: string | null) => void;
  onSelect: (id: number | null) => void;
}): JSX.Element {
  const items = result.items;
  // W-B1 keyboard walk over the visible typeahead hits (-1 = none).
  const [highlight, setHighlight] = useState(-1);

  /** Open a typeahead hit DIRECTLY: land in its group with the detail
   *  panel open (query resets to the new scope). */
  const openItem = (item: TreatmentCatalogItemView): void => {
    setHighlight(-1);
    onGroup(item.serviceGroup);
    onSelect(item.id);
  };

  // Level 1: RESTING state = service groups (derived from the data, never
  // hardcoded -- the S1 disclosure law). TYPING flips to the W-B1
  // TYPE-AHEAD: procedure hits by NAME or CODE across the loaded scope,
  // grouped under still-tappable group headings, each hit opening its
  // price/provenance detail directly.
  if (group === null) {
    const browsing = query.trim() === '';
    const groups = browsing ? filterCatalogGroups(items, query) : [];
    const taGroups = typeaheadCatalogResults(items, query);
    const taFlat = typeaheadFlatItems(taGroups);
    return (
      <div className="catalog-browse">
        <input
          type="search"
          className="clinic-workbench__input treatment-picker__search"
          data-testid="catalog-search"
          placeholder="Search treatments by name or code"
          aria-label="Search treatments by name or code"
          value={query}
          autoComplete="off"
          onChange={(e) => {
            setHighlight(-1);
            onQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (taFlat.length > 0) {
                setHighlight((h) => Math.min(h + 1, taFlat.length - 1));
              }
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (taFlat.length > 0) setHighlight((h) => Math.max(h - 1, 0));
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              const pick =
                highlight >= 0 && highlight < taFlat.length
                  ? taFlat[highlight]
                  : taFlat[0];
              if (pick !== undefined) {
                openItem(pick);
              } else if (taGroups.length === 1 && taGroups[0] !== undefined) {
                setHighlight(-1);
                onGroup(taGroups[0].group);
              }
              return;
            }
            if (e.key === 'Escape' && query !== '') {
              e.preventDefault();
              setHighlight(-1);
              onQuery('');
            }
          }}
        />
        {browsing ? (
          groups.length === 0 ? (
            <p className="treatment-picker__empty" data-testid="catalog-no-results">
              No catalog match.
            </p>
          ) : (
            <div className="treatment-picker__list" data-testid="catalog-groups">
              {groups.map((g) => (
                <button
                  key={g.group}
                  type="button"
                  className="treatment-picker__option"
                  data-testid={`catalog-group-${g.group}`}
                  onClick={() => onGroup(g.group)}
                >
                  <span className="treatment-picker__option-name">{g.group}</span>
                  <span className="treatment-picker__option-meta">
                    {g.itemCount} {g.itemCount === 1 ? 'procedure' : 'procedures'}
                  </span>
                </button>
              ))}
            </div>
          )
        ) : taGroups.length === 0 ? (
          <p className="treatment-picker__empty" data-testid="catalog-no-results">
            No catalog match.
          </p>
        ) : (
          <div className="treatment-picker__list" data-testid="catalog-typeahead">
            {taGroups.map((g) => (
              <div key={g.group} className="treatment-picker__ta-group">
                <button
                  type="button"
                  className="treatment-picker__option treatment-picker__group-head"
                  data-testid={`catalog-group-${g.group}`}
                  onClick={() => {
                    setHighlight(-1);
                    onGroup(g.group);
                  }}
                >
                  <span className="treatment-picker__option-name">{g.group}</span>
                  <span className="treatment-picker__option-meta">
                    {g.itemCount} {g.itemCount === 1 ? 'procedure' : 'procedures'}
                  </span>
                </button>
                {g.items.map((item) => {
                  const active =
                    highlight >= 0 && taFlat[highlight]?.id === item.id;
                  return (
                    <CatalogProcedureRow
                      key={item.id}
                      item={item}
                      selected={false}
                      active={active}
                      scopeIsClinic={scopeIsClinic}
                      onSelect={() => openItem(item)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {result.capped ? (
          <p className="treatment-picker__note">
            Showing the first {items.length} items - refine the search.
          </p>
        ) : null}
      </div>
    );
  }

  // Level 2: procedures within the selected group only.
  const scoped = filterItemsWithinGroup(items, group, query);
  const selected =
    selectedId === null
      ? null
      : (scoped.find((i) => i.id === selectedId) ?? null);
  return (
    <div className="catalog-browse">
      <button
        type="button"
        className="treatment-picker__escape"
        data-testid="catalog-back-to-groups"
        onClick={() => onGroup(null)}
      >
        All groups
      </button>
      <input
        type="search"
        className="clinic-workbench__input treatment-picker__search"
        data-testid="catalog-search"
        placeholder="Search within this group"
        aria-label="Search within this group"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      {scoped.length === 0 ? (
        <p className="treatment-picker__empty" data-testid="catalog-no-results">
          No catalog match.
        </p>
      ) : (
        <div className="treatment-picker__list" data-testid="catalog-procedures">
          {scoped.map((item) => (
            <CatalogProcedureRow
              key={item.id}
              item={item}
              selected={selectedId === item.id}
              scopeIsClinic={scopeIsClinic}
              onSelect={() => onSelect(selectedId === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}
      {selected !== null ? (
        <div className="catalog-browse__detail" data-testid="catalog-detail">
          <h3 className="clinic-table__subtitle">{selected.serviceName}</h3>
          <FieldGrid
            fields={catalogDetailFields(selected)}
            label={`Details for ${selected.serviceName}`}
            testId="catalog-detail-fields"
          />
          {/* W-B3/S-4: an unset price NAMES who can fix it. No in-product
              price writer exists for ANY role (read-only catalog; founder
              Studio seed; the V5-P5/V8-P7 admin seam) -- naming, never a
              dead edit affordance. */}
          {catalogPriceSource(selected) === 'not_configured' ? (
            <p
              className="treatment-picker__note"
              data-testid="catalog-price-naming"
            >
              {PRICE_SETTER_NAMING_COPY}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CatalogProcedureRow({
  item,
  selected,
  scopeIsClinic,
  onSelect,
  active = false,
}: {
  item: TreatmentCatalogItemView;
  selected: boolean;
  scopeIsClinic: boolean;
  onSelect: () => void;
  /** W-B1 keyboard highlight (typeahead level only). */
  active?: boolean;
}): JSX.Element {
  const source = catalogPriceSource(item);
  return (
    <button
      type="button"
      className={
        selected
          ? 'treatment-picker__option treatment-picker__option--selected'
          : active
            ? 'treatment-picker__option treatment-picker__option--active'
            : 'treatment-picker__option'
      }
      data-testid={`catalog-item-${item.id}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="treatment-picker__option-name">{item.serviceName}</span>
      <span className="treatment-picker__option-meta">
        {pricingUnitLabel(item.pricingUnit)}
      </span>
      <span
        className="treatment-picker__option-default"
        data-testid={`catalog-item-price-${item.id}`}
      >
        {catalogPriceDisplay(item)}
      </span>
      {source !== 'not_configured' ? (
        <span
          className="treatment-picker__source"
          data-testid={`catalog-item-source-${item.id}`}
        >
          {catalogPriceSourceLabel(source)}
        </span>
      ) : null}
      {!scopeIsClinic && item.clinicId !== null ? (
        // Org-wide provenance view: an unresolved clinic override names
        // itself (rows ship with clinicId visible per the read contract).
        <AcBadge tone="info" testId={`catalog-item-override-${item.id}`}>
          Clinic {item.clinicId} override
        </AcBadge>
      ) : null}
      {!item.active ? (
        <AcBadge tone="pending" testId={`catalog-item-inactive-${item.id}`}>
          Inactive
        </AcBadge>
      ) : null}
    </button>
  );
}
